import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Gift, MapPin } from "lucide-react";
import Image from "next/image";
import { useLanguage } from "@/contexts/LanguageContext";
import { DashboardCreateLink } from "@/components/dashboardCreateLink/dashboardCreateLink";

const FEATURE_KEYS = [
  "pricing.f1",
  "pricing.f2",
  "pricing.f3",
  "pricing.f4",
  "pricing.f5",
  "pricing.f6",
  "pricing.f7",
  "pricing.f8",
  "pricing.f9",
  "pricing.f10",
] as const;

const interFont = {
  fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
} as const;

export default function MenoPricingV2() {
  const { t, isRTL } = useLanguage();
  const [isYearly, setIsYearly] = useState(true);
  const textAlign = "text-start";

  return (
    <section
      id="pricing"
      className="scroll-mt-20 bg-white py-16 lg:py-24 safari-paint-contain"
    >
      <div className="custom-container">
        <motion.div
          className="overflow-hidden rounded-3xl border border-pink-100/60 bg-[#FFF5F9] px-6 py-12 sm:px-10 sm:py-14 lg:px-14 lg:py-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-10 max-w-3xl text-center lg:mb-14"
          >
            <span
              className="mb-5 inline-flex items-center rounded-full bg-white px-4 py-1.5 text-xs font-bold tracking-wide text-gray-500 shadow-sm"
              style={interFont}
            >
              {t("pricingV2.badge")}
            </span>

            <h2 style={{ lineHeight: 0 }}>
              <span
                className={`text-3xl font-extrabold text-gray-900 sm:text-4xl lg:text-5xl`}
                style={{ ...interFont, lineHeight: isRTL ? 1.5 : 1.2 }}
              >
                {t("pricingV2.title1")}{" "}
                <span className="text-meno-pink block">
                  {t("pricingV2.titleAccent")}
                </span>
              </span>
            </h2>

            <p style={{ lineHeight: 0 }}>
              <span
                className="mx-auto mt-4 block max-w-2xl text-base leading-relaxed text-gray-500 sm:text-lg"
                style={interFont}
              >
                {t("pricingV2.sub")}
              </span>
            </p>
          </motion.div>

          {/* Cards */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2 lg:gap-8"
          >
            {/* Left — Pricing card */}
            <div
              className="flex flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-xl shadow-gray-900/5"
              style={interFont}
            >
              {/* Pink header bar */}
              <div className="flex flex-col gap-4 bg-meno-pink px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5">
                    <Image
                      src="/images/meno/meno-logo.svg"
                      alt="Meno"
                      width={72}
                      height={18}
                      className="h-[18px] w-auto"
                    />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      |
                    </span>
                    <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white">
                      {t("pricingV2.pro")}
                    </span>
                  </div>
                </div>

                <div className="inline-flex items-center gap-1 rounded-full bg-white/20 p-1 self-start sm:self-auto">
                  <button
                    type="button"
                    onClick={() => setIsYearly(false)}
                    className="relative rounded-full px-4 py-1.5 text-xs font-bold text-white transition"
                  >
                    {!isYearly && (
                      <motion.span
                        layoutId="pricing-v2-toggle"
                        transition={{ duration: 0.22, ease: "easeInOut" }}
                        className="pointer-events-none absolute inset-0 rounded-full bg-white shadow-sm"
                      />
                    )}
                    <span
                      className={`relative z-10 ${!isYearly ? "text-meno-pink" : ""}`}
                    >
                      {t("pricing.monthly")}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsYearly(true)}
                    className="relative rounded-full px-4 py-1.5 text-xs font-bold text-white transition"
                  >
                    {isYearly && (
                      <motion.span
                        layoutId="pricing-v2-toggle"
                        transition={{ duration: 0.22, ease: "easeInOut" }}
                        className="pointer-events-none absolute inset-0 rounded-full bg-white shadow-sm"
                      />
                    )}
                    <span
                      className={`relative z-10 ${isYearly ? "text-meno-pink" : ""}`}
                    >
                      {t("pricing.yearly")}
                    </span>
                  </button>
                </div>
              </div>

              <div className="flex flex-1 flex-col p-6 sm:p-8">
                {/* Price row */}
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                  <div className={textAlign}>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
                        {isYearly
                          ? t("pricingV2.priceYearly")
                          : t("pricingV2.priceMonthly")}
                      </span>
                      <span className="text-lg font-semibold text-gray-500">
                        {t("pricingV2.perMonth")}
                      </span>
                    </div>
                    <p style={{ lineHeight: 0 }}>
                      <span className="mt-1 block text-sm text-gray-400">
                        {isYearly
                          ? t("pricingV2.priceYearlyNote")
                          : t("pricingV2.priceMonthlyNote")}
                      </span>
                    </p>
                  </div>

                  {isYearly && (
                    <span className="rounded-full bg-[#E3FCF0] px-3 py-1 text-[11px] font-extrabold tracking-wide text-[#2D8B6F]">
                      {t("pricingV2.saveBadge")}
                    </span>
                  )}
                </div>

                {/* Base plan pill */}
                <div className="mb-6 inline-flex w-fit items-center gap-2 rounded-full bg-[#FFE1EC] px-3.5 py-2">
                  <MapPin
                    aria-hidden="true"
                    className="h-3.5 w-3.5 shrink-0 text-meno-pink"
                    strokeWidth={2.5}
                  />
                  <span className="text-xs font-semibold text-gray-700">
                    {t("pricingV2.basePlan")}
                  </span>
                </div>

                {/* Outlet tiers */}
                <div className="mb-4">
                  <p style={{ lineHeight: 0 }}>
                    <span
                      className={`mb-3 block text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 ${textAlign}`}
                    >
                      {t("pricingV2.outletsTitle")}
                    </span>
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
                      <span className="mb-3 inline-block rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold text-gray-600">
                        {t("pricingV2.outlet1_10")}
                      </span>
                      <p style={{ lineHeight: 0 }}>
                        <span className="inline-flex flex-wrap items-baseline gap-x-1">
                          <span className="text-xl font-extrabold text-gray-900 sm:text-2xl">
                            {isYearly
                              ? t("pricingV2.outlet1_10AmountYearly")
                              : t("pricingV2.outlet1_10AmountMonthly")}
                          </span>
                          <span className="text-sm font-medium text-gray-400">
                            {t("pricingV2.outletPriceUnit")}
                          </span>
                        </span>
                      </p>
                    </div>
                    <div className="rounded-2xl border p-4 sm:p-5">
                      <span className="mb-3 inline-block rounded-full bg-[#FFE1EC] px-3 py-1 text-[11px] font-semibold text-meno-pink">
                        {t("pricingV2.outlet11plus")}
                      </span>
                      <p style={{ lineHeight: 0 }}>
                        <span className="inline-flex flex-wrap items-baseline gap-x-1">
                          <span className="text-xl font-extrabold text-gray-900 sm:text-2xl">
                            {isYearly
                              ? t("pricingV2.outlet11plusAmountYearly")
                              : t("pricingV2.outlet11plusAmountMonthly")}
                          </span>
                          <span className="text-sm font-medium text-gray-400">
                            {t("pricingV2.outletPriceUnit")}
                          </span>
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <p style={{ lineHeight: 0 }}>
                  <span
                    className={`mb-6 block text-xs leading-relaxed text-gray-400 ${textAlign}`}
                  >
                    {t("pricingV2.commissionNote")}
                  </span>
                </p>

                {/* Free trial banner */}
                <div className="mb-6 flex items-start gap-3 rounded-2xl bg-[#E3FCF0] p-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white">
                    <Gift
                      aria-hidden="true"
                      className="h-5 w-5 text-[#2D8B6F]"
                      strokeWidth={2.5}
                    />
                  </div>
                  <div className={textAlign}>
                    <p style={{ lineHeight: 0 }}>
                      <span className="block text-sm font-bold text-gray-900">
                        {t("pricingV2.trialTitle")}
                      </span>
                    </p>
                    <p style={{ lineHeight: 0 }}>
                      <span className="mt-1 block text-xs text-gray-500">
                        {t("pricingV2.trialDesc")}
                      </span>
                    </p>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-auto">
                  <DashboardCreateLink
                    meno
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full rounded-2xl bg-meno-pink py-4 text-center text-sm font-extrabold text-white shadow-lg shadow-meno-pink/25 transition hover:brightness-105 active:scale-[0.98] sm:text-base"
                  >
                    {t("pricingV2.cta")}
                  </DashboardCreateLink>
                </div>
              </div>
            </div>

            {/* Right — Features card */}
            <div
              className="relative flex flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-900/5 sm:p-8"
              style={interFont}
            >
              <div className="mb-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFE1EC]">
                    <Check
                      aria-hidden="true"
                      className="h-4 w-4 text-meno-pink"
                      strokeWidth={3}
                    />
                  </div>
                  <h3 style={{ lineHeight: 0 }}>
                    <span className="text-lg font-extrabold text-gray-900">
                      {t("pricingV2.featuresTitle")}
                    </span>
                  </h3>
                </div>
                <span className="shrink-0 rounded-full bg-meno-pink px-3 py-1 text-[10px] font-extrabold tracking-wide text-white">
                  {t("pricingV2.featuresBadge")}
                </span>
              </div>

              <ul className="relative z-10 flex flex-col gap-4 p-0">
                {FEATURE_KEYS.map((key) => (
                  <li key={key} className="flex items-start gap-3">
                    <Check
                      aria-hidden="true"
                      className="mt-0.5 h-4 w-4 shrink-0 text-meno-pink"
                      strokeWidth={3}
                    />
                    <span className="text-sm font-medium leading-snug text-gray-700">
                      {t(key)}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Watermark */}
              <div
                className={`pointer-events-none absolute -bottom-10 ${isRTL ? "-left-12" : "right-0"} opacity-[0.04]`}
                aria-hidden="true"
              >
                <Image
                  src="/images/meno/meno-element.svg"
                  alt=""
                  width={280}
                  height={70}
                  className="h-auto w-[280px] translate-x-8 translate-y-8"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
